import { cloneDeep } from '@fi-shared/utils';
import { Component, Injector, Input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MediaBankFileModel } from '../media-bank-file.model';
import { MediaBankEditBase } from '../media-bank-edit-base';
import { StatisticService } from '@fi-shared/statistic/statistic.service';
import { SaveChangesWarningService } from '@fi-shared/fi-shared-services/save-changes-warning.service';
import { FileTypeService } from '@fi-shared/fi-shared-services/file-type.service';
import { ObjectTypes } from '@fi-shared/types/object-type';
import { ModuleType } from '@fi-shared/types/module-type';
import { FileUploadModel } from '@fi-app/utilities/file-upload/file-upload.model';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { ObjectTitleService } from '@fi-app/utilities/fi-services/object-title.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { ILightWeightObject } from '@fi-shared/models';
import { FiContentLanguageSharedService } from '@fi-shared/fi-content-language-shared/fi-content-language-shared.service';
import { FileType } from '@fi-shared/types';
import { FiMediaStorageService } from '@fi-shared/media-bank/fi-media-storage.service';
import { FiFileUpload2Service } from '@fi-shared/fi-insert-media/fi-file-upload2.service';
import { BehaviorSubject } from 'rxjs';

@Component({
    selector: 'fi-media-bank-edit',
    templateUrl: './media-bank-edit.component.html',
    styleUrls: ['./media-bank-edit.component.scss'],
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-media-bank-form-edit' },
    standalone: false,
})
export class MediaBankEditComponent extends MediaBankEditBase implements OnInit {
    @Input('mediaBankFile') MediaBankFile: MediaBankFileModel;

    FileUploadModel: FileUploadModel;
    ShowUploadedFile: boolean = false;
    isPreviewVisible$ = new BehaviorSubject<boolean>(true);
    useDefaultVimeoPreview$ = new BehaviorSubject<boolean>(false);
    selectedTagIds: number[] = [];
    constructor(
        injector: Injector,
        private statisticService: StatisticService,
        private saveChangesWarningService: SaveChangesWarningService,
        private fileTypeService: FileTypeService,
        private stateService: FiStateService,
        private objectTitleService: ObjectTitleService,
        private mediaStorageService: FiMediaStorageService,
        private fileUploadService: FiFileUpload2Service,
        private fiContentLanguageSharedService: FiContentLanguageSharedService,
    ) {
        super(injector);
    }

    ngOnInit() {
        super.onInit(this.objectTitleService.getEditTitle(this.MediaBankFile.Name));
        this.selectedTagIds = [...this.MediaBankFile.TagIds];
        this.FileUploadModel = new FileUploadModel(($files) => {
            if ($files.length > 0) {
                const newFileType = this.fileTypeService.getFileType($files[0]);
                const currentFileType = this.MediaBankFile.FileType;

                const isVimeoVideos =
                    this.mediaStorageService.isVimeoStorage(this.MediaBankFile.StorageType) &&
                    this.isMovieFileType(newFileType);
                const isCurrentFileVideoNoVimeo =
                    !this.mediaStorageService.isVimeoStorage(this.MediaBankFile.StorageType) &&
                    this.isMovieFileType(this.MediaBankFile.FileType);

                // replacing of movies from object storage (old format) by vimeo ones is not implemented
                if ((newFileType !== currentFileType && !isVimeoVideos) || isCurrentFileVideoNoVimeo) {
                    this.fiDialogsService.error({
                        messageText: this.appTextService.get('media_bank.replace_error_file_format'),
                    });
                    return false;
                }

                this.ShowUploadedFile = true;
                this.uploadErrors = [];

                this.mediaBankService
                    .replaceFile($files[0], this.MediaBankFile, (uploadError) => this.uploadErrors.push(uploadError))
                    .finally(() => {
                        this.useDefaultVimeoPreview$.next(true);
                        this.isPreviewVisible$.next(false);
                        setTimeout(() => {
                            this.isPreviewVisible$.next(true);
                        });
                    });
            }
        });

        this.FileUploadModel.accept = this.FileUploadModel.getAcceptByFileTypes([this.MediaBankFile.FileType]);

        this.saveChangesWarningService.start(() => {
            const mediaBankFile = cloneDeep(this.MediaBankFile);
            // Ensure that the PublicationStartDate is a Date object
            if (typeof mediaBankFile.PublicationSetting.PublicationStartDate === 'string') {
                mediaBankFile.PublicationSetting.PublicationStartDate = new Date(
                    mediaBankFile.PublicationSetting.PublicationStartDate,
                );
            }
            return mediaBankFile;
        });

        this.statisticService.Visit(ModuleType.MediaBank, ObjectTypes.MediaBank, this.MediaBankFile.Id);
        this.selectedLanguageId = this.fiContentLanguageSharedService.getContentLanguageIdByContentTranslation(
            this.MediaBankFile.Names,
        );
    }

    private isMovieFileType(fileType: FileType): boolean {
        return fileType === FileType.Movie || fileType === FileType.MovieNotPlayable;
    }

    TagsDataSource() {
        return this.mediaBankService.getMediaBankFileTagsByUserAccessRights();
    }

    addNewTag(tagName: string): void {
        super.addNewTag(tagName, (tag) => {
            this.availableTags.push(tag);
            this.selectedTagIds = [...this.selectedTagIds, tag.Id];
        });
    }

    Save() {
        if (
            this.MediaBankFile.PercentageLoaded &&
            this.MediaBankFile.PercentageLoaded < 100 &&
            this.MediaBankFile.PercentageProcessed < 100
        ) {
            this.fiDialogsService.notify({
                messageText: this.appTextService.get('media_bank.save_error_upload_not_complete'),
            });
            return;
        }

        this.MediaBankFile.Url = '';
        this.MediaBankFile.TagIds = [...this.selectedTagIds];
        this.mediaBankService.SaveMediaBankFile(this.MediaBankFile).then(() => {
            this.accessRightFilters.OnAccessRightFilterCollectionSaved();

            this.saveChangesWarningService.stop();
            this.stateService.go(FiStateNames.MediaBank.List);
            this.showSnackbar(this.MediaBankFile.PublicationSetting.IsShared, 1);
        });
    }

    protected readonly ObjectTypes = ObjectTypes;
}
